import type { Skill, SkillCategory } from '../../../types/skill'
import { SkillTag } from '../../atoms/SkillTag/SkillTag'
import styles from './SkillsSection.module.css'

/* ── Données statiques des compétences ────────── */
const SKILLS: Skill[] = [
  // Architecture & Design Patterns
  { label: 'Clean Architecture',      url: 'https://blog.cleancoder.com/uncle-bob/2012/08/13/the-clean-architecture.html', category: 'architecture' },
  { label: 'Microservices',           url: 'https://microservices.io/',                                                   category: 'architecture' },
  { label: 'SOLID Principles',        url: 'https://en.wikipedia.org/wiki/SOLID',                                         category: 'architecture' },
  { label: 'DRY Principle',           url: 'https://en.wikipedia.org/wiki/Don%27t_repeat_yourself',                       category: 'architecture' },
  { label: 'REST API Design',         url: 'https://restfulapi.net/',                                                     category: 'architecture' },
  { label: 'GraphQL',                 url: 'https://graphql.org/',                                                        category: 'architecture' },
  { label: 'OpenAPI (Swagger)',       url: 'https://www.openapis.org/',                                                   category: 'architecture' },
  { label: 'HTTP Protocol',           url: 'https://developer.mozilla.org/en-US/docs/Web/HTTP',                           category: 'architecture' },
  { label: 'Event-Driven Architecture', url: 'https://en.wikipedia.org/wiki/Event-driven_architecture',                   category: 'architecture' },

  // Databases
  { label: 'Relational Databases',    url: 'https://en.wikipedia.org/wiki/Relational_database',                           category: 'database' },
  { label: 'NoSQL Databases',         url: 'https://en.wikipedia.org/wiki/NoSQL',                                         category: 'database' },
  { label: 'Database Design',         url: 'https://en.wikipedia.org/wiki/Database_normalization',                        category: 'database' },
  { label: 'Caching Strategies',      url: 'https://redis.io/docs/manual/patterns/',                                      category: 'database' },

  // Security
  { label: 'OAuth 2.0',              url: 'https://oauth.net/2/',                                                         category: 'security' },
  { label: 'JWT Authentication',      url: 'https://jwt.io/',                                                             category: 'security' },
  { label: 'API Security',            url: 'https://owasp.org/www-project-api-security/',                                 category: 'security' },

  // DevOps
  { label: 'Containerization',        url: 'https://www.docker.com/resources/what-container/',                            category: 'devops' },
  { label: 'CI/CD Pipelines',         url: 'https://about.gitlab.com/topics/ci-cd/',                                      category: 'devops' },
  { label: 'Cloud Computing',         url: 'https://aws.amazon.com/what-is-cloud-computing/',                             category: 'devops' },
  { label: 'Serverless Architecture', url: 'https://en.wikipedia.org/wiki/Serverless_computing',                          category: 'devops' },
  { label: 'Web Hosting',             url: 'https://en.wikipedia.org/wiki/Web_hosting_service',                           category: 'devops' },

  // Tooling
  { label: 'Git Version Control',     url: 'https://git-scm.com/doc',                                                    category: 'tooling' },
  { label: 'Git Workflow',            url: 'https://guides.github.com/introduction/flow/',                                category: 'tooling' },
  { label: 'Code Review',             url: 'https://en.wikipedia.org/wiki/Code_review',                                   category: 'tooling' },
  { label: 'TDD',                     url: 'https://en.wikipedia.org/wiki/Test-driven_development',                       category: 'tooling' },
  { label: 'Application Logging',     url: 'https://en.wikipedia.org/wiki/Logging_(software)',                            category: 'tooling' },
  { label: 'Performance Monitoring',  url: 'https://en.wikipedia.org/wiki/Application_performance_management',            category: 'tooling' },
  { label: 'Technical Documentation', url: 'https://www.markdownguide.org/',                                              category: 'tooling' },
  { label: 'API Documentation',       url: 'https://en.wikipedia.org/wiki/Software_documentation',                        category: 'tooling' },

  // System
  { label: 'Linux Systems',           url: 'https://www.kernel.org/',                                                    category: 'system' },
  { label: 'Shell Scripting',         url: 'https://www.gnu.org/software/bash/manual/',                                  category: 'system' },
  { label: 'CLI Tools',               url: 'https://en.wikipedia.org/wiki/Command-line_interface',                        category: 'system' },
  { label: 'Memory Management',       url: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Memory_Management', category: 'system' },
  { label: 'Concurrency & Threading', url: 'https://en.wikipedia.org/wiki/Concurrency_(computer_science)',                category: 'system' },
]

/* Libellés lisibles pour les catégories */
const CATEGORY_LABELS: Record<SkillCategory, string> = {
  architecture: 'Architecture & Design',
  database:     'Bases de données',
  security:     'Sécurité',
  devops:       'DevOps & Cloud',
  tooling:      'Outillage',
  system:       'Système',
}

/* Ordre d'affichage des catégories */
const CATEGORY_ORDER: SkillCategory[] = [
  'architecture',
  'database',
  'security',
  'devops',
  'tooling',
  'system',
]

export function SkillsSection() {
  /*
   * Regroupement des skills par catégorie dans l'ordre défini.
   * On utilise un Map pour préserver l'ordre d'insertion.
   */
  const grouped = CATEGORY_ORDER.reduce<Map<SkillCategory, Skill[]>>((acc, cat) => {
    acc.set(cat, SKILLS.filter((s) => s.category === cat))
    return acc
  }, new Map())

  return (
    <section id="skills" className={styles.section} aria-labelledby="skills-title">
      {/* ── En-tête ── */}
      <div className={styles.header}>
        <h2 id="skills-title" className={styles.title}>
          Compétences
        </h2>
        {/* Barre décorative gradient */}
        <div className={styles.titleBar} aria-hidden="true" />
      </div>

      {/* ── Groupes de compétences ── */}
      <div className={styles.groups}>
        {CATEGORY_ORDER.map((cat) => {
          const skills = grouped.get(cat) ?? []
          if (skills.length === 0) return null

          return (
            <div key={cat} className={styles.group}>
              {/* Label de catégorie */}
              <h3 className={styles.groupLabel} data-category={cat}>
                {CATEGORY_LABELS[cat]}
              </h3>

              {/* Tags */}
              <ul className={styles.tagList} aria-label={`Compétences : ${CATEGORY_LABELS[cat]}`}>
                {skills.map((skill) => (
                  <li key={skill.label}>
                    <SkillTag skill={skill} />
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </section>
  )
}